import type { paths } from '@/types/api/v1' // generated by openapi-typescript
import { getAPIURL } from '@/utils/api'
import { NotificationMessage } from '@gravitate-js/excalibrr'
import type { QueryClient, QueryFilters, SetDataOptions } from '@tanstack/react-query'
import { isArray } from 'lodash'
import createFetchClient from 'openapi-fetch'
import type { MaybeOptionalInit } from 'openapi-fetch'
import createClient from 'openapi-react-query'
import type { HttpMethod, PathsWithMethod } from 'openapi-typescript-helpers'
import { useMemo } from 'react'

let cachedToken: string | undefined
const getToken = () => {
  if (!cachedToken) {
    const result = localStorage.getItem('token')
    if (result) {
      cachedToken = result
    }
  }
  return cachedToken
}

const fetchClient = createFetchClient<paths>({
  baseUrl: getAPIURL(),
})

fetchClient.use({
  onRequest: ({ request }) => {
    const authHeader = getToken()
    if (authHeader) request.headers.set('Authorization', `Bearer ${authHeader}`)
    return request
  },
  onResponse: async ({ response }) => {
    if (response.ok) return
    const body = response.clone()

    const errDetails = await body
      .json()
      .then((result): string[] => {
        if (!result.detail) return []
        if (typeof result.detail === 'string') return [result.detail]
        if (isArray(result.detail)) {
          return (result.detail as { loc?: string[]; msg?: string }[])
            .filter((d) => (d.loc?.length ?? 0) > 0)
            .flatMap((d) => d.loc!.slice(1).map((l) => `${l.replace('_', ' ')} - ${d.msg}`))
        }
        return []
      })
      .catch((err) => {
        console.log('could not get json response: ', err)
        return [] as string[]
      })

    if (!errDetails.length) {
      NotificationMessage('Oops', 'Something unexpected happened. If this issue continues, please contact support.')
      return
    }

    if (errDetails[0] === 'Not enough scopes for this endpoint') {
      NotificationMessage(
        'You are not allowed access. ',
        'if you feel this is a mistake, please contact your system administrator'
      )
      return
    }

    errDetails.forEach((d) => {
      NotificationMessage('Oops!', d)
    })
  },
})
export const $api = createClient(fetchClient)

export const useApiClient = () => useMemo(() => $api, [])

export type InferRequest<
  Path extends keyof paths,
  Method extends keyof paths[Path] = 'post',
> = paths[Path][Method] extends {
  requestBody: { content: { 'application/json': infer X } }
}
  ? X
  : never

export type InferResponse<
  Path extends keyof paths,
  Method extends keyof paths[Path] = 'post',
> = paths[Path][Method] extends {
  responses: { '200': { content: { 'application/json': infer X } } }
}
  ? X
  : never

type PathQueryFilters<T> = Omit<QueryFilters, 'predicate' | 'queryKey'> & { init?: T }
type Updater<T> = ((response?: T) => T | undefined) | T

export type InferFullRequest<M extends HttpMethod, Path extends PathsWithMethod<paths, M>> = MaybeOptionalInit<
  paths[Path],
  M
>

//these util types are mostly the result of typescript throwing an 'excessively deep instantiation' error
//when the queryKey functions are passed as arguments directly
//todo: investigate if we can remove these after typescript upgrade; there is reason to believe
//that the above issue is caused by NoInfer<> not being supported currently.
export const getQueryKeys = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  method: M,
  path: Path,
  init?: MaybeOptionalInit<paths[Path], M>
) => (init === undefined ? ([method, path] as const) : ([method, path, init] as const))

export const getQueryFilters = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  method: M,
  path: Path,
  options?: PathQueryFilters<MaybeOptionalInit<paths[Path], M>>
) => {
  const queryKey = getQueryKeys(method, path, options?.init)
  return { ...options, queryKey }
}

export const getTypedQueriesData = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  client: QueryClient,
  method: M,
  path: Path,
  options?: PathQueryFilters<MaybeOptionalInit<paths[Path], M>>
) => {
  const filters = getQueryFilters(method, path, options)
  return client.getQueriesData<InferResponse<Path, M>>(filters) as [typeof filters.queryKey, InferResponse<Path, M>][]
}
export const getTypedQueryData = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  client: QueryClient,
  method: M,
  path: Path,
  init?: MaybeOptionalInit<paths[Path], M>
) => {
  const queryKey = getQueryKeys(method, path, init)
  return client.getQueryData<InferResponse<Path, M>>(queryKey)
}

export const setTypedQueriesData = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  client: QueryClient,
  method: M,
  path: Path,
  filterOptions: PathQueryFilters<MaybeOptionalInit<paths[Path], M>>,
  updater: Updater<InferResponse<Path, M>>,
  options?: SetDataOptions
) => {
  const filters = getQueryFilters(method, path, filterOptions)
  return client.setQueriesData<InferResponse<Path, M>>(filters, updater, options) as [
    typeof filters.queryKey,
    InferResponse<Path, M> | undefined,
  ][]
}

export const setTypedQueryData = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  client: QueryClient,
  method: M,
  path: Path,
  params: { init?: MaybeOptionalInit<paths[Path], M> },
  updater: Updater<InferResponse<Path, M>>,
  options?: SetDataOptions
) => {
  const queryKey = getQueryKeys(method, path, params.init)
  return client.setQueryData<InferResponse<Path, M>>(queryKey, updater, options) as [
    typeof queryKey,
    InferResponse<Path, M> | undefined,
  ][]
}

export const cancelTypedQueries = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  client: QueryClient,
  method: M,
  path: Path,
  init?: MaybeOptionalInit<paths[Path], M>
) => client.cancelQueries(getQueryFilters(method, path, { init }))

export const resetTypedQueries = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  client: QueryClient,
  method: M,
  path: Path,
  init?: MaybeOptionalInit<paths[Path], M>
) => client.resetQueries(getQueryFilters(method, path, { init }))

export const removeTypedQueries = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  client: QueryClient,
  method: M,
  path: Path,
  init?: MaybeOptionalInit<paths[Path], M>
) => client.removeQueries(getQueryFilters(method, path, { init }))

export const invalidateTypedQueries = <M extends HttpMethod, Path extends PathsWithMethod<paths, M>>(
  client: QueryClient,
  method: M,
  path: Path,
  init?: MaybeOptionalInit<paths[Path], M>
) => client.invalidateQueries(getQueryFilters(method, path, { init }))
